/**
 * letterEngineConfig.ts — config silnika formatek dla klasy Pismo (R3, mirror
 * `documentEngineConfig.tsx` / `invoiceEngineConfig.ts`).
 *
 * Pismo: 13 pól core w ZESTAWIE identycznych z commercial/internal (seed
 * `apps/api/lib/seed-system-modules.ts`, slug 'letters'), WŁĄCZNIE z
 * `system_number` (R3 ujednolicenie — decyzja Alexandra): numer systemowy jest
 * teraz pokazywany na zakładce „Dane" (read-only, placeholder w create), tak
 * jak commercial/internal/umowy/faktury. Zakładka „Dane" = 13 pól.
 *
 * Nadpisania własne (`letterCoreRenderers.tsx`, patrz jego nagłówek dla pełnego
 * uzasadnienia): `system_number` (read-only + placeholder), `status`
 * (LETTER_STATUSES), `urgency` (DOCUMENT_URGENCIES + etykiety pism — poprawna
 * polszczyzna „Pilne"), `source` (read-only na sztywno),
 * `externalId`/`externalSource` (disable warunkowy cross-field).
 * `ownerUserId` reużywa WSPÓLNE nadpisanie (UserPicker).
 *
 * Reszta (title/documentDate/description/notes/rejectionReason) — NATYWNA
 * delegacja do ExtFieldValueInput. `labelSource:'definition'` (Z3,
 * SILNIK-FORMATEK-ROLLOUT.md §2): etykiety z definicji pola (field.name), NIE
 * z i18n — odstępstwo świadome od pilota commercial/internal (i18nLabelSource),
 * mirror decyzji Z3 zastosowanej już przy fakturach (`invoiceEngineConfig.ts`).
 *
 * Uwaga: `description`/`notes`/`rejectionReason` mają ground-truth
 * `fieldType:'text'` w seedzie (mirror commercial/internal) → natywna delegacja
 * renderuje je jako `<Textarea>` (ExtFieldValueInput, branch 'text'). Przed
 * migracją `LetterDataTab` renderował `description` jako jednowierszowy
 * `TextInput` (niekonsekwencja z commercial/internal, które już wtedy miały
 * Textarea) — silnik ZBIEGA `description` do tego samego widgetu co
 * commercial/internal (Textarea), drobne poszerzenie UI, bez zmiany
 * maxLength/save-payload.
 */

import type {
  CoreFieldConfig,
  KartotekaEngineConfig,
} from '@/components/kartoteki/engine/engine-config';

import { makeDocumentCategoryRenderer } from './documentCategoryRenderer';
import { documentCompanyFieldConfig } from './documentCompanyRenderer';
import {
  letterExternalIdRenderer,
  letterExternalSourceRenderer,
  letterOwnerUserIdRenderer,
  letterSourceRenderer,
  letterStatusRenderer,
  letterSystemNumberRenderer,
  letterUrgencyRenderer,
} from './letterCoreRenderers';
import type { LetterFormData } from './letterFormTypes';

/**
 * Mapa slug → config pola core. `external_id`/`external_source` NIE mają
 * `maxLength` w configu — limit trzyma ich renderer (200/100); config maxLength
 * byłby martwy (silnik czyta go tylko dla pól z natywną delegacją). `maxLength`
 * zostaje TYLKO przy title/description (natywna delegacja → ExtFieldValueInput).
 */
const LETTER_FIELDS: Record<string, CoreFieldConfig<LetterFormData>> = {
  system_number: {
    stateKey: 'systemNumber',
    fieldType: 'varchar',
    renderer: letterSystemNumberRenderer,
  },
  title: { stateKey: 'title', fieldType: 'varchar', colSpan: 2, maxLength: 500 },
  status: { stateKey: 'status', fieldType: 'select', renderer: letterStatusRenderer },
  document_date: { stateKey: 'documentDate', fieldType: 'date' },
  description: { stateKey: 'description', fieldType: 'text', colSpan: 2, maxLength: 1000 },
  urgency: { stateKey: 'urgency', fieldType: 'select', renderer: letterUrgencyRenderer },
  owner_user_id: {
    stateKey: 'ownerUserId',
    fieldType: 'reference',
    renderer: letterOwnerUserIdRenderer,
  },
  category_id: {
    stateKey: 'categoryId',
    fieldType: 'select',
    renderer: makeDocumentCategoryRenderer<LetterFormData>(),
  },
  notes: { stateKey: 'notes', fieldType: 'text', colSpan: 2, rows: 3 },
  rejection_reason: { stateKey: 'rejectionReason', fieldType: 'text', colSpan: 2, rows: 2 },
  source: { stateKey: 'source', fieldType: 'select', renderer: letterSourceRenderer },
  external_id: { stateKey: 'externalId', fieldType: 'varchar', renderer: letterExternalIdRenderer },
  external_source: {
    stateKey: 'externalSource',
    fieldType: 'varchar',
    renderer: letterExternalSourceRenderer,
  },
  // Firma dokumentu — tylko do odczytu, opt-in z układu (NIE w coreFieldSlugs): patrz documentCompanyRenderer.tsx.
  company_id: documentCompanyFieldConfig<LetterFormData>(),
};

/**
 * Slugi core w kolejności seeda (system_number pierwszy) — konsumuje top-up
 * strony (synteza zakładki 'data' przez `topUpDocumentLayout` gdy brak aktywnego
 * layoutu).
 */
const LETTER_CORE_FIELD_SLUGS = [
  'system_number',
  'title',
  'status',
  'document_date',
  'description',
  'urgency',
  'owner_user_id',
  'category_id',
  'notes',
  'rejection_reason',
  'source',
  'external_id',
  'external_source',
];

export const letterEngineConfig: KartotekaEngineConfig<LetterFormData> = {
  moduleSlug: 'letters',
  testIdPrefix: 'letter-field',
  coreFieldSlugs: LETTER_CORE_FIELD_SLUGS,
  fields: LETTER_FIELDS,
  labelSource: 'definition',
  coerceEmptyToNull: true,
};
